import React from "react";
import { Separator, Text, Box, Button } from "@showitinc/showit-wc";
import ViewPanel from "../../blogsetup/ViewPanel";
import SetupButtonBar from "../../blogsetup/SetupButtonBar";
import Constants from "../Constants";
import HelpConstants from "../../../constants/HelpConstants";
import useOpenAccount, { PATH_DOMAINS, WINDOW_NEW } from "../../../hooks/useOpenAccount";

function ErrorContent({ message }) {
  return (
    <>
      <Text variant="heading-2" variantStyle="strong">
        Domain Saved
      </Text>
      <Separator />
      <Box mb="2">
        <Text>Your domain has been set up successfully.</Text>
      </Box>
      <Box mb="2">
        <div className="has-error">
          <div className="help-block">
            {message
              ? `There was a problem submitting your blog setup request: ${message}`
              : "There was a problem submitting your blog setup request."}{" "}
            You can set up your blog later from the site settings.
          </div>
        </div>
      </Box>
    </>
  );
}

function BlogRequestContent({ isMigration }) {
  return (
    <>
      <Text variant="heading-2" variantStyle="strong">
        {isMigration ? "Your migration request has been received!" : "Your new blog request has been received!"}
      </Text>
      <Separator />
      <Box mb="2">
        <Text>
          Once your blog is set up, we will contact you with instructions to point your domain name
          to your new blog server.
        </Text>
      </Box>
      <Box mb="2">
        <Text>
          After you have configured your domain DNS settings, please contact support to add an SSL
          certificate. This will secure your website and complete the domain setup process.
        </Text>
      </Box>
      {isMigration && (
        <Box mb="2">
          <Text variant="body-2" variantStyle="semi">
            Starting now, please refrain from entering new posts or changes to your blog as those
            may be lost during migration. Once we notify you that it is ready, you can resume your
            blog posting.
          </Text>
        </Box>
      )}
      <Box mb="2">
        <Text>
          See{" "}
          <a href={HelpConstants.URL_BLOG_SETUP} target="_blank" rel="noreferrer">
            this article
          </a>{" "}
          for estimated turnaround time.
        </Text>
      </Box>
    </>
  );
}

function DomainOnlyContent({ onConfigureDns }) {
  return (
    <>
      <Text variant="heading-2" variantStyle="strong">
        Success
      </Text>
      <Separator />
      <Box mb="2">
        <Text variant="body-2" variantStyle="semi">
          Your domain information has been successfully saved in Showit.
        </Text>
      </Box>
      <Box mb="2">
        <Text variant="body-2">Next, point your domain to Showit&apos;s servers:</Text>
      </Box>
      <Box mb="2">
        <Button variant="feature" size="md" onSIClick={onConfigureDns}>
          Configure DNS Settings
        </Button>
      </Box>
    </>
  );
}

function Complete({ blogSetup, actions }) {
  const { path, blogSubmitError, blogSubmitErrorMessage } = blogSetup;
  const [openAccountDomains] = useOpenAccount({ path: PATH_DOMAINS, windowTarget: WINDOW_NEW });

  const hasBlog = path !== Constants.PATH_NO_BLOG;
  const isMigration = path === Constants.PATH_ADV_MIGRATE;

  return (
    <ViewPanel>
      {{
        content: (
          <>
            {blogSubmitError && <ErrorContent message={blogSubmitErrorMessage} />}
            {!blogSubmitError && hasBlog && <BlogRequestContent isMigration={isMigration} />}
            {!blogSubmitError && !hasBlog && <DomainOnlyContent onConfigureDns={openAccountDomains} />}
          </>
        ),
        actions: (
          <SetupButtonBar
            onForward={actions.close}
            allowForward
            allowBack={false}
            forwardBtnText="Done"
          />
        ),
      }}
    </ViewPanel>
  );
}

export default Complete;
